import { ApolloClient, InMemoryCache, ApolloLink } from '@apollo/client/core'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { createUploadLink } from 'apollo-upload-client'
import { useSnackbarStore } from '@/stores/snackbar'
import { useDialogStore } from '@/stores/dialog'
import { toast } from 'vue-sonner'

const cache = new InMemoryCache()

const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(
            ({ debugMessage, message, extensions: { category } }) => {
                if (category !== 'validation') {
                    const snackbarStore = useSnackbarStore()
                    snackbarStore.open({
                        message: debugMessage || message,
                        type: 'error',
                        timeout: -1,
                    })
                }
            }
        )
    }

    if (networkError && networkError.statusCode === 419) {
        const dialogStore = useDialogStore()

        dialogStore
            .confirm({
                title: 'Session expired',
                message: 'You need to refresh the page.',
                agreeButtonText: 'Refresh',
                agreeButtonColor: 'blue-accent-4',
                cancellable: false,
            })
            .then(() => window.location.reload())
    }

    if (networkError && networkError.statusCode === 503) {
        toast.error('503 Service Unavailable', {
            description:
                'The server is temporarily unable to service your request due to maintenance downtime or capacity problems. Please try again later.',
            duration: 3600000,
        })
    }
})

const authLink = setContext((_, { headers }) => {
    const regex = RegExp('XSRF-TOKEN[^;]+').exec(document.cookie)
    const token = decodeURIComponent(
        regex ? regex.toString().replace(/^[^=]+./, '') : ''
    )

    return {
        headers: {
            ...headers,
            'X-XSRF-TOKEN': token,
        },
    }
})

export const defaultApolloClient = new ApolloClient({
    link: ApolloLink.from([
        errorLink,
        authLink,
        createUploadLink({
            uri: import.meta.env.VITE_APP_GRAPHQL_DEFAULT_HTTP,
            credentials: 'include',
        }),
    ]),
    cache,
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'network-only',
        },
        query: {
            fetchPolicy: 'network-only',
        },
    },
})
